import { DecimalPipe } from '@angular/common';
import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Workbook } from 'exceljs';
import * as fs from 'file-saver';
import {
  SpecialAttributes,
  UserList,
} from '@core/interfaces/credentials.model';
import { CredentialsService } from '@core/services/credentials/credentials.service';
import { CountryService } from '@core/services/sort.service';
import { NgbdSortableHeader } from '@core/services/sort/sortEvent.directive';
import { UtilsService } from '@core/services/utils/utils.service';
import { ModalCredentialsComponent } from '@shared/modals/modal-credentials/modal-credentials.component';
import { Subject, take, takeUntil } from 'rxjs';

@Component({
  selector: 'app-special-attributes-table',
  templateUrl: './special-attributes-table.component.html',
  styleUrls: ['./special-attributes-table.component.scss'],
  providers: [CountryService, DecimalPipe],
})
export class SpecialAttributesTableComponent implements OnInit, OnDestroy {
  /** Descarga Excel de atributos (Gestor de Clientes). En Registro nuevo Cliente suele ir en false. */
  @Input() showExcelDownload = true;
  /** Si es false, no carga hasta que se llame refreshPagination/refreshList (p. ej. tab Atributos en Gestor). */
  @Input() loadOnInit = true;

  page = 1;
  pageSize = 10;
  collectionSize: number;
  characterSearch: string = '';
  loading = false;
  loadingExcel = true;
  counter = Array;
  listSpecialUserShow: Array<SpecialAttributes> = [];
  listSpecialUser: Array<SpecialAttributes> = [];
  excelData: Array<SpecialAttributes> = [];
  responseListSpecialUser: UserList;
  errorListSpecialUser = false;
  errorSpecialAttributes: number;
  typeCheckbox: string;
  valueSelectCheckbox: boolean;

  private unsubscribe$ = new Subject<void>();

  @ViewChildren(NgbdSortableHeader) headers!: QueryList<NgbdSortableHeader>;

  constructor(
    public service: CountryService,
    private dialog: MatDialog,
    public _credentialsService: CredentialsService,
    public _utilsService: UtilsService
  ) {}

  ngOnInit(): void {
    if (this.loadOnInit) {
      this.specialAttributesList(this.characterSearch);
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  /** Alias para compatibilidad con pantallas que refrescan tras cargas masivas. */
  refreshList(): void {
    this.refreshPagination();
  }

  specialAttributesList(valueText: string): void {
    this.cancelRequest();
    this.loading = true;
    this.listSpecialUserShow = [];
    this.listSpecialUser = [];
    const dataCredentials = {
      characterSearch: valueText?.replace(/&/g, '%26').replace(/#/g, '%23'),
      page: this.page,
      pageSize: this.pageSize,
      isPaginate: true,
    };
    this._credentialsService
      .getSpecialAttributesList(dataCredentials)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (res) => {
          this.errorListSpecialUser = false;
          this.loading = false;
          this.responseListSpecialUser = res;
          this.listSpecialUser = res?.data.map((element: any) => {
            const temp: any = {
              ...element,
              pickUpStatus: element.pickUp.find((a) => a.available === true)
                ?.available
                ? true
                : false,
            };
            return temp;
          });
          this.listSpecialUserShow = this.listSpecialUser;
          this.collectionSize = this.responseListSpecialUser.totalCount;
        },
        error: (err) => {
          this.loading = false;
          this.errorListSpecialUser = true;
          this.errorSpecialAttributes = err?.status;
        },
      });
  }

  cancelRequest(): void {
    this.unsubscribe$.next();
  }

  refreshPagination(): void {
    this.specialAttributesList(this.characterSearch);
  }

  searchList(value: string): void {
    if ((value && value.length >= 3) || value.length === 0) {
      this.page = 1;
      this.specialAttributesList(value);
    } else {
      this.listSpecialUserShow = this.listSpecialUser;
      this.collectionSize = this.responseListSpecialUser?.totalCount;
    }
  }

  onChange(
    value: any,
    codeSelect: string,
    slide: any,
    pickUpStatus: boolean,
    i: number
  ): void {
    slide.checked = pickUpStatus;
    value.codeSelect = codeSelect;
    const dialogRef = this.dialog.open(ModalCredentialsComponent, {
      width: '35rem',
      data: value,
    });
    dialogRef.afterClosed().subscribe((data: any) => {
      if (data) {
        this._credentialsService
          .getUpdatepickup(data)
          .pipe(take(1))
          .subscribe({
            next: () => {
              this.listSpecialUser[i].pickUp = data;
              slide.checked = this.listSpecialUser[i].pickUp.find(
                (a) => a.available === true
              )?.available
                ? true
                : false;
              this._utilsService.snackBarUpload(
                'Genial! Usuario se actualizó correctamente',
                'save'
              );
            },
            error: () => {
              slide.checked = pickUpStatus;
              this._utilsService.snackBarUpload(
                `Error!. Intenta guardar nuevamente`,
                'warning-l'
              );
            },
          });
      }
    });
  }

  onChangeCheckbox(
    valuecheckbox: any,
    type: string,
    i: number,
    country: SpecialAttributes
  ): void {
    switch (type) {
      case 'ally':
        this.listSpecialUserShow[i].ally = !valuecheckbox;
        this.typeCheckbox = 'Aliados';
        this.valueSelectCheckbox = this.listSpecialUserShow[i].ally;
        break;
      case 'benefit':
        this.listSpecialUserShow[i].benefit = !valuecheckbox;
        this.typeCheckbox = 'Reportes';
        this.valueSelectCheckbox = this.listSpecialUserShow[i].benefit;
        break;
      case 'advance':
        this.listSpecialUserShow[i].advance = !valuecheckbox;
        this.typeCheckbox = 'Avances';
        this.valueSelectCheckbox = this.listSpecialUserShow[i].advance;
        break;
      case 'partner':
        this.listSpecialUserShow[i].partner = !valuecheckbox;
        this.typeCheckbox = 'Socios';
        this.valueSelectCheckbox = this.listSpecialUserShow[i].partner;
        break;
    }
    const dataBody = {
      code: country.code,
      field: this.typeCheckbox,
      value: this.valueSelectCheckbox,
    };
    this._credentialsService
      .getUpdateCustomerSetting(dataBody)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this._utilsService.snackBarUpload(
            'Genial! Usuario se actualizó correctamente',
            'save'
          );
        },
        error: () => {
          this._utilsService.snackBarUpload(
            `Error!. Intenta guardar nuevamente`,
            'warning-l'
          );
        },
      });
  }

  onSort({ column, direction }: any): void {
    this.headers.forEach((header) => {
      if (header.sortable !== column) {
        header.direction = '';
      }
    });
    this.service.sortColumn = column;
    this.service.sortDirection = direction;
  }

  downloadExcel(): void {
    this.loadingExcel = false;
    const dataCredentials = {
      characterSearch: this.characterSearch
        ?.replace(/&/g, '%26')
        .replace(/#/g, '%23'),
      page: this.page,
      pageSize: this.pageSize,
      isPaginate: false,
    };
    this._credentialsService
      .getSpecialAttributesList(dataCredentials)
      .pipe(take(1))
      .subscribe({
        next: (res) => {
          this.excelData = res?.data.map((element: any) => {
            const temp: any = {
              ...element,
              pickUpStatus: element.pickUp.find((a) => a.available === true)
                ?.available
                ? true
                : false,
            };
            return temp;
          });
          this.converDateEcel();
        },
        error: (err) => {
          this.loadingExcel = true;
          this.errorSpecialAttributes = err?.status;
        },
      });
  }

  converDateEcel(): void {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Worksheet');
    const length = this.excelData.length;
    const header = [
      `Cod cliente`,
      `Cliente`,
      `Correo`,
      `Aliados Puntos`,
      `Cliente recoge`,
      `Reporte de reconocimientos`,
      `Avance`,
      `Aliados Beneficios`,
    ];
    worksheet.addRow(header);

    worksheet.getCell('A1').font = { bold: true, size: 11 };
    worksheet.getCell('B1').font = { bold: true, size: 11 };
    worksheet.getCell('C1').font = { bold: true, size: 11 };
    worksheet.getCell('D1').font = { bold: true, size: 11 };
    worksheet.getCell('E1').font = { bold: true, size: 11 };
    worksheet.getCell('F1').font = { bold: true, size: 11 };
    worksheet.getCell('G1').font = { bold: true, size: 11 };
    worksheet.getCell('H1').font = { bold: true, size: 11 };

    worksheet.autoFilter = `A1:H${length + 2}`;

    const units: any[] = [];
    this.excelData.map((u) => {
      const tempArr = [
        `${u.code}`,
        `${u.name}`,
        `${u.email}`,
        `${u.ally}`,
        `${u.pickUpStatus}`,
        `${u.benefit}`,
        `${u.advance}`,
        `${u.partner}`,
      ];
      units.push(tempArr);
    });
    worksheet.addRows(units);

    worksheet.columns = [
      { width: 12 },
      { width: 40 },
      { width: 26 },
      { width: 12 },
      { width: 12 },
      { width: 15 },
      { width: 15 },
      { width: 15 },
    ];

    const fname = 'Clientes';
    workbook.xlsx.writeBuffer().then((data) => {
      const blob = new Blob([data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      fs.saveAs(blob, fname + '.xlsx');
    });
    this.loadingExcel = true;
  }
}
